// src/pages/Configuracion.js
//
// Configuración del negocio: datos generales de la empresa activa
// (pos.Empresas). Editable, guardado real vía PUT /api/pos/empresas/{id}.
//
// Módulos que se agregan más adelante:
//  - C7: modo de operación de cajas (caja_unica / multi_caja / vendedor_a_caja)
//  - C8: configuración de facturación DIAN (resolución, prefijo, consecutivo)
//  - C10: permisos de usuarios

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Stack, TextField, Button,
  Divider, MenuItem, Alert, CircularProgress, Tabs, Tab, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlaceIcon from '@mui/icons-material/Place';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { empresas, sucursales as sucursalesApi, parametricas, ubicacion } from '../api/posApi';
import MapaSucursal from '../components/MapaSucursal';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>{value === index && children}</Box>
);

const Configuracion = () => {
  const { empresa, refrescarEmpresa, notify, cargando, errorCarga, reintentarCargaEmpresa } = useApp();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // Sucursal principal (donde viven las coordenadas). Se busca al cargar
  // la empresa. Cuando el negocio tiene una sola sucursal (caso HolaVeci
  // hoy), esa es la que se edita. Cuando en el futuro haya varias,
  // agregamos un selector.
  const [sucursal, setSucursal] = useState(null);
  const [coords, setCoords] = useState({ lat: '', lng: '' });
  const [coordsDirty, setCoordsDirty] = useState(false);

  // Dropdowns
  const [regimenes, setRegimenes] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [municipios, setMunicipios] = useState([]);

  // Cargar dropdowns una sola vez
  useEffect(() => {
    parametricas.regimenesTributarios().then(setRegimenes).catch(() => {});
    ubicacion.tiposDocumentoIdentidad().then(setTiposDoc).catch(() => {});
  }, []);

  // Cargar municipios cuando el form tiene departamento (aplazado a que
  // el usuario abra el select por primera vez; por ahora traemos todos
  // filtrados por buscar).
  const buscarMunicipios = useCallback(async (q) => {
    if (!q || q.length < 3) { setMunicipios([]); return; }
    try {
      const r = await ubicacion.municipios(undefined, q);
      setMunicipios(Array.isArray(r) ? r : []);
    } catch { /* ignore */ }
  }, []);

  // Hidratar form desde empresa
  useEffect(() => {
    if (!empresa) { setForm(null); return; }
    setForm({
      empresaId:              empresa.empresaId || empresa.EmpresaId,
      codigo:                 empresa.codigo || empresa.Codigo || '',
      razonSocial:            empresa.razonSocial || empresa.RazonSocial || '',
      nombreComercial:        empresa.nombreComercial || empresa.NombreComercial || '',
      nit:                    empresa.nit || empresa.NIT || '',
      digitoVerificacion:     empresa.digitoVerificacion || empresa.DigitoVerificacion || '',
      tipoDocIdentidadId:     empresa.tipoDocIdentidadId || empresa.TipoDocIdentidadId || null,
      tipoOrganizacionId:     empresa.tipoOrganizacionId || empresa.TipoOrganizacionId || null,
      tipoEntidadId:          empresa.tipoEntidadId || empresa.TipoEntidadId || null,
      regimentTributarioId:   empresa.regimentTributarioId || empresa.RegimentTributarioId || null,
      direccion:              empresa.direccion || empresa.Direccion || '',
      municipioID:            empresa.municipioID || empresa.MunicipioID || null,
      municipioNombre:        empresa.municipioNombre || empresa.MunicipioNombre || '',
      telefono:               empresa.telefono || empresa.Telefono || '',
      email:                  empresa.email || empresa.Email || '',
      sitioWeb:               empresa.sitioWeb || empresa.SitioWeb || '',
      logoUrl:                empresa.logoUrl || empresa.LogoUrl || '',
      colorPrimario:          empresa.colorPrimario || empresa.ColorPrimario || '',
      colorSecundario:        empresa.colorSecundario || empresa.ColorSecundario || '',
    });
    setDirty(false);
  }, [empresa]);

  // Cargar la sucursal principal de la empresa activa
  useEffect(() => {
    if (!empresa) { setSucursal(null); setCoords({ lat: '', lng: '' }); return; }
    const empresaId = empresa.empresaId || empresa.EmpresaId;
    let cancelado = false;
    (async () => {
      try {
        const lista = await sucursalesApi.listar(empresaId);
        if (cancelado) return;
        const items = Array.isArray(lista) ? lista : (lista.items || []);
        // Preferir la marcada como principal; si no hay, la primera activa
        const principal = items.find(s => s.esPrincipal || s.EsPrincipal) || items[0];
        if (!principal) return;
        setSucursal(principal);
        setCoords({
          lat: principal.latitud ?? principal.Latitud ?? '',
          lng: principal.longitud ?? principal.Longitud ?? '',
        });
        setCoordsDirty(false);
      } catch { /* silencioso */ }
    })();
    return () => { cancelado = true; };
  }, [empresa]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const guardar = async () => {
    if (!form?.empresaId) return;
    setGuardando(true); setError('');
    try {
      // 1. Actualizar empresa (datos generales + tributario)
      if (dirty) {
        await empresas.actualizar(form.empresaId, {
          codigo:               form.codigo,
          razonSocial:          form.razonSocial,
          nombreComercial:      form.nombreComercial,
          nit:                  form.nit,
          digitoVerificacion:   form.digitoVerificacion,
          tipoDocIdentidadId:   form.tipoDocIdentidadId,
          tipoOrganizacionId:   form.tipoOrganizacionId,
          tipoEntidadId:        form.tipoEntidadId,
          regimentTributarioId: form.regimentTributarioId,
          direccion:            form.direccion,
          municipioID:          form.municipioID,
          telefono:             form.telefono,
          email:                form.email,
          sitioWeb:             form.sitioWeb,
          logoUrl:              form.logoUrl,
          colorPrimario:        form.colorPrimario,
          colorSecundario:      form.colorSecundario,
        });
      }
      // 2. Actualizar coordenadas de la sucursal principal si cambiaron
      if (coordsDirty && sucursal) {
        const sucId = sucursal.sucursalId || sucursal.SucursalId;
        await sucursalesApi.actualizar(sucId, {
          empresaId:   sucursal.empresaId || sucursal.EmpresaId,
          codigo:      sucursal.codigo || sucursal.Codigo,
          nombre:      sucursal.nombre || sucursal.Nombre,
          nombreCorto: sucursal.nombreCorto || sucursal.NombreCorto,
          esPrincipal: sucursal.esPrincipal ?? sucursal.EsPrincipal ?? true,
          direccion:   sucursal.direccion || sucursal.Direccion,
          municipioID: sucursal.municipioID || sucursal.MunicipioID,
          telefono:    sucursal.telefono || sucursal.Telefono,
          email:       sucursal.email || sucursal.Email,
          latitud:     coords.lat === '' ? null : parseFloat(coords.lat),
          longitud:    coords.lng === '' ? null : parseFloat(coords.lng),
        });
      }
      await refrescarEmpresa();
      notify('Configuración guardada.', 'success');
      setDirty(false); setCoordsDirty(false);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorCarga) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" startIcon={<RefreshIcon />}
                  onClick={reintentarCargaEmpresa}>
            Reintentar
          </Button>
        }
      >
        <Typography variant="body2" fontWeight={600}>
          No se pudo cargar la empresa
        </Typography>
        <Typography variant="caption">{errorCarga}</Typography>
        {user?.entidadTipo === 'NEGOCIO' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            Tu ID de sucursal es <b>{user.entidadId}</b>. Verifica que esa sucursal
            exista en <code>pos.Sucursales</code> y que tenga <code>EmpresaId</code> asignado.
          </Typography>
        )}
      </Alert>
    );
  }

  if (!empresa) {
    if (user?.entidadTipo === 'SUPERADMIN') {
      return (
        <Alert severity="info">
          Selecciona una empresa desde el header para editar su configuración.
        </Alert>
      );
    }
    return (
      <Alert severity="warning">
        Tu cuenta no tiene una empresa asociada. Contacta al soporte.
      </Alert>
    );
  }

  if (!form) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <StorefrontIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h4" fontWeight={700}>Configuración</Typography>
          <Typography variant="body2" color="text.secondary">
            Datos de la empresa {form.razonSocial}
          </Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<StorefrontIcon />} iconPosition="start" label="Datos generales" />
          <Tab icon={<ReceiptIcon />}    iconPosition="start" label="Tributario" />
        </Tabs>

        <CardContent>
          {/* ─── DATOS GENERALES ─── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Código interno" value={form.codigo || ''}
                           onChange={(e) => set('codigo', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Razón social" value={form.razonSocial || ''}
                           onChange={(e) => set('razonSocial', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nombre comercial" value={form.nombreComercial || ''}
                           onChange={(e) => set('nombreComercial', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="NIT" value={form.nit || ''}
                           onChange={(e) => set('nit', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth label="DV" value={form.digitoVerificacion || ''}
                           onChange={(e) => set('digitoVerificacion', e.target.value)} />
              </Grid>
              <Grid item xs={12}><Divider><Chip label="Contacto" size="small" /></Divider></Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Dirección" value={form.direccion || ''}
                           onChange={(e) => set('direccion', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Municipio"
                  value={form.municipioNombre || ''}
                  onChange={(e) => {
                    set('municipioNombre', e.target.value);
                    buscarMunicipios(e.target.value);
                  }}
                  helperText={
                    municipios.length > 0
                      ? `Sugerencias: ${municipios.slice(0, 5).map(m => m.nombre || m.Nombre).join(', ')}`
                      : 'Escribe 3+ caracteres para buscar'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Teléfono" value={form.telefono || ''}
                           onChange={(e) => set('telefono', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Correo" type="email" value={form.email || ''}
                           onChange={(e) => set('email', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Sitio web" value={form.sitioWeb || ''}
                           onChange={(e) => set('sitioWeb', e.target.value)} />
              </Grid>
              <Grid item xs={12}><Divider><Chip label="Marca" size="small" /></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Color primario" placeholder="#0f4c3a"
                           value={form.colorPrimario || ''}
                           onChange={(e) => set('colorPrimario', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Color secundario"
                           value={form.colorSecundario || ''}
                           onChange={(e) => set('colorSecundario', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="URL del logo (opcional)"
                           value={form.logoUrl || ''}
                           onChange={(e) => set('logoUrl', e.target.value)} />
              </Grid>

              {/* ─── Ubicación en el mapa ─── */}
              <Grid item xs={12}>
                <Divider sx={{ mt: 2 }}><Chip label="Ubicación en el mapa" size="small" icon={<PlaceIcon />} /></Divider>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1, mb: 1.5 }}>
                  Estas coordenadas identifican tu sucursal principal. Los vecinos las
                  usan para buscar negocios cercanos.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <MapaSucursal
                  lat={coords.lat === '' ? null : parseFloat(coords.lat)}
                  lng={coords.lng === '' ? null : parseFloat(coords.lng)}
                  onCoordsChange={(lat, lng) => {
                    setCoords({ lat: lat.toFixed(7), lng: lng.toFixed(7) });
                    setCoordsDirty(true);
                  }}
                  notify={notify}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ─── TRIBUTARIO ─── */}
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Régimen tributario"
                           value={form.regimentTributarioId || ''}
                           onChange={(e) => set('regimentTributarioId', e.target.value || null)}>
                  <MenuItem value="">(sin especificar)</MenuItem>
                  {regimenes.map((r) => (
                    <MenuItem key={r.id || r.Id} value={r.id || r.Id}>
                      {r.nombre || r.Nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Tipo de documento"
                           value={form.tipoDocIdentidadId || ''}
                           onChange={(e) => set('tipoDocIdentidadId', e.target.value || null)}>
                  <MenuItem value="">(sin especificar)</MenuItem>
                  {tiposDoc.map((t) => (
                    <MenuItem key={t.id || t.Id || t.tipoDocumentoIdentidadID}
                              value={t.id || t.Id || t.tipoDocumentoIdentidadID}>
                      {t.codigo || t.Codigo} — {t.nombre || t.Nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  La configuración de facturación electrónica DIAN (resolución,
                  prefijo, consecutivo) se activa en una tanda posterior.
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>

          <Divider sx={{ my: 3 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button disabled={(!dirty && !coordsDirty) || guardando} onClick={() => {
              setForm((f) => ({ ...f }));
              refrescarEmpresa();
            }}>Cancelar</Button>
            <Button variant="contained" startIcon={<SaveIcon />}
                    onClick={guardar} disabled={(!dirty && !coordsDirty) || guardando}>
              {guardando ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Configuracion;
